<script setup lang="ts">
import { ref } from "vue";

import { caseFileService } from "@/services";
import { fromUtc } from "@/services/UtilitiesService";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";
import { useCaseFileStore } from "@/stores/case-file-store";

import type { Metadata } from "@/models/Metadata";
import type { SHA256MatchedFile } from "@/services/CaseFileService";

withDefaults(defineProps<{ file: Metadata }>(), {  })

const routeParams = useCaseRouteParams();

const isInitialCheck = ref<boolean>();
const fileStatus = ref<{ type: "LOADING" | "MATCHES" | "NOT_MATCHES" | "ERROR" | "FILE_ERROR" }>();
const matchedLedgers = ref<SHA256MatchedFile[]>();

const uploadDate = (targetDate: string | undefined) => {
  const formattedDate = fromUtc(targetDate, { timeStyle: "medium" });
  return formattedDate === "" ? "" : `completed at ${formattedDate}`;
};

function selectFile(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  verifyHash(file);
  fileStatus.value = undefined;
}

async function verifyHash(file?: File) {
  if (routeParams.value.casefile == null) return;

  let fileBuffer: ArrayBuffer | undefined;
  try {
    fileBuffer = await file?.arrayBuffer();
  } catch (error) {
    console.error(error);
    fileStatus.value = { type: "FILE_ERROR" };
    return;
  }

  const hashBytes = fileBuffer == null ? undefined : await window.crypto.subtle.digest("SHA-256", fileBuffer);
  const hash = new Uint8Array(hashBytes ?? []).reduce((acc, x) => acc + x.toString(16).padStart(2, "0"), "");

  fileStatus.value = { type: "LOADING" };

  try {
    matchedLedgers.value = await caseFileService.verifySHA256Hash(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      routeParams.value.casefile,
      hash,
    );
    fileStatus.value = { type: "MATCHES" };
  } catch (error) {
    console.error(error);
    const status = (error as { status: number }).status;
    matchedLedgers.value = [];
    if (status === 409) fileStatus.value = { type: "NOT_MATCHES" };
    else {
      fileStatus.value = { type: "ERROR" };
      return;
    }
  }

  isInitialCheck.value = isInitialCheck.value == null;
}
</script>
<template>
  <div class="tw-size-full tw-flex-1 tw-overflow-auto tw-bg-gray-100/50 tw-p-2">
    <p
      class="tw-mb-4 tw-max-w-xl tw-text-pretty tw-rounded-lg tw-p-2"
      :class="{
        'tw-bg-red-200 tw-text-red-900': fileStatus?.type === 'NOT_MATCHES',
        'tw-bg-green-200 tw-text-green-900': fileStatus?.type === 'MATCHES',
        'tw-border tw-border-black tw-bg-transparent': fileStatus == null || fileStatus?.type === 'LOADING',
        'tw-bg-orange-200 tw-text-orange-900': fileStatus?.type === 'ERROR' || fileStatus?.type === 'FILE_ERROR',
      }"
    >
      <template v-if="fileStatus == null">
        <i class="far fa-circle-question" />
        Click the button below to compare the current record to the original upload.
      </template>
      <template v-else-if="fileStatus.type === 'LOADING'">
        <i class="fas fa-spinner tw-animate-spin" />
        Checking...
      </template>
      <template v-else-if="fileStatus.type === 'MATCHES'">
        <i class="far fa-circle-check" />
        <template v-if="matchedLedgers![0].matched">
          The {{ isInitialCheck === true ? "current record" : "selected file" }} matches the original upload
          {{ uploadDate(file.additionalMetadata?.uploadDate) }}
        </template>
        <template v-else-if="matchedLedgers![matchedLedgers!.length - 1].matched">
          The {{ isInitialCheck === true ? "current record" : "selected file" }} is the latest variant of the original
          upload {{ uploadDate(matchedLedgers![matchedLedgers!.length - 1].created) }}
        </template>
        <template v-else>
          The {{ isInitialCheck === true ? "current record" : "selected file" }} does not match the original upload,
          however, it is a known variation of the original upload
          {{ uploadDate(matchedLedgers?.find((t) => t.matched)?.created) }}
        </template>
      </template>
      <template v-else-if="fileStatus.type === 'NOT_MATCHES'">
        <i class="far fa-circle-xmark" />
        The {{ isInitialCheck === true ? "current record" : "selected file" }} does not match the original upload
        {{ uploadDate(file.additionalMetadata?.uploadDate) }}
      </template>
      <template v-else-if="fileStatus.type === 'FILE_ERROR'">
        <i class="far fa-circle-question" />
        The selected file could not be processed. Please try again.
      </template>
      <template v-else-if="fileStatus.type === 'ERROR'">
        <i class="far fa-circle-question" />
        Unknown error while comparing the hashes. Please try again or report to support
      </template>
    </p>
    <div class="tw-flex tw-flex-col tw-gap-2">
      <label
        for="fileSelect"
        class="tw-text-sm tw-font-medium tw-leading-none peer-disabled:tw-cursor-not-allowed peer-disabled:tw-opacity-70"
      >
        <template v-if="isInitialCheck == null"> Selected Current Record </template>
        <template v-else>Compare the authentic record with an external file</template>
      </label>
      <div v-if="isInitialCheck == null" class="tw-flex tw-flex-wrap tw-gap-2 [&>*]:tw-h-10">
        <div
          class="tw-flex tw-size-full tw-max-w-sm tw-cursor-not-allowed tw-items-center tw-rounded-md tw-border tw-border-black/20 tw-bg-white tw-p-2"
        >
          <span class="tw-truncate tw-text-sm">
            {{ useCaseFileStore().currentFile?.displayName }}
          </span>
        </div>
        <button
          type="button"
          class="invert tw-min-w-0 tw-rounded-md tw-bg-black tw-px-3 tw-font-medium tw-text-white"
          @click="() => verifyHash()"
        >
          Compare with Original Upload
        </button>
      </div>
      <input
        v-else
        id="fileSelect"
        type="file"
        class="tw-flex tw-h-10 tw-w-full tw-max-w-sm tw-cursor-pointer tw-rounded-md tw-border tw-border-black/20 tw-bg-white tw-text-sm file:tw-h-full file:tw-cursor-pointer file:tw-border-0 file:tw-bg-black file:tw-text-sm file:tw-font-medium file:tw-text-white"
        @change="selectFile"
      />

      <div v-if="!!matchedLedgers?.length" class="tw-flex tw-flex-col tw-gap-2">
        <h3 class="tw-text-lg tw-font-semibold">Ledger Records</h3>
        <ul class="tw-list-disc tw-pl-5">
          <li v-for="ledger in matchedLedgers" :key="ledger.hash">
            <span class="tw-font-medium" :class="{ 'tw-font-bold': ledger.matched }">{{
              fromUtc(ledger.created, {
                timeStyle: "medium",
              })
            }}</span>
            <template v-if="!!ledger.metadata.operation"
              ><em>Created via {{ ledger.metadata.operation }}</em></template
            >
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
